<script>
import BalanceTransaction from '@/model/dataLayer/api/MembershipSystem/BalanceTransaction'
import DisplayInfoTitleShow from '@/views/AadenPages/memberShipReborn/Common/DisplayInfoTitleShow.vue'
import GlobalConfig from '@/model/global/GlobalSettings'
import PriceTableDisplay from '@/views/widget/typography/PriceTableDisplay.vue'
import UserDetailsDialog from '@/views/AadenPages/memberShipReborn/userManagement/UserDetailsDialog/index.vue'
import DateRangePicker from '@/views/widget/commonLogic/DateRangePicker.vue'
import { mapMutations } from 'vuex'

export default {
  name: 'RechargeTransactionManagement',
  components: {
    DisplayInfoTitleShow,
    PriceTableDisplay,
    UserDetailsDialog,
    DateRangePicker
  },
  data () {
    return {
      loading: false,
      transactions: [],
      selectedTransaction: null,
      detailsDialog: false,
      userDetailsDialog: false,
      selectedUserId: null,
      totalAmount: 0,
      search: '',
      businessLayerId: GlobalConfig.blId || null,
      deviceId: GlobalConfig.DeviceId || '', // Add deviceId even though it's not used in API
      transactionType: 'ALL', // ALL, RECHARGE
      dateRange: [null, null],
      dateMenu: false,

      // Table headers
      headers: [
        { text: '交易ID', value: 'id', sortable: true },
        { text: '用户ID', value: 'userId', sortable: true },
        { text: '交易金额', value: 'amount', sortable: true, width: '150px' },
        { text: '交易类型', value: 'transactionType', sortable: true },
        { text: '交易描述', value: 'description', sortable: true },
        { text: '充值套餐ID', value: 'rechargePackageId', sortable: true },
        { text: '支付意图ID', value: 'paymentIntentId', sortable: true },
        { text: '交易时间', value: 'createTimestamp', sortable: true },
        { text: '操作', value: 'actions', sortable: false }
      ],

      // Transaction type options
      transactionTypeOptions: [
        { text: '全部', value: 'ALL' },
        { text: '充值', value: 'RECHARGE' }
      ],

      // Pagination
      pagination: {
        page: 1,
        itemsPerPage: 10,
        pageCount: 0,
        itemsLength: 0
      }
    }
  },
  computed: {
    // Date range text computed property
    dateRangeText () {
      if (!this.dateRange[0] || !this.dateRange[1]) return ''
      return `${this.dateRange[0]} ~ ${this.dateRange[1]}`
    },

    // Formatted total amount
    formattedTotalAmount () {
      return this.formatPrice(this.totalAmount)
    },

    // Filtered transactions based on search, transaction type, and date range
    filteredTransactions () {
      let filtered = [...this.transactions]

      // Apply transaction type filter
      if (this.transactionType !== 'ALL') {
        filtered = filtered.filter(item => item.transactionType === this.transactionType)
      }

      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        filtered = filtered.filter(item =>
          (item.id && item.id.toString().includes(searchLower)) ||
            (item.userId && item.userId.toLowerCase().includes(searchLower)) ||
            (item.description && item.description.toLowerCase().includes(searchLower)) ||
            (item.paymentIntentId && item.paymentIntentId.toLowerCase().includes(searchLower)) ||
            (item.rechargePackageId && item.rechargePackageId.toString().includes(searchLower))
        )
      }

      // Apply date range filter
      if (this.dateRange[0] && this.dateRange[1]) {
        const startDate = new Date(this.dateRange[0])
        const endDate = new Date(this.dateRange[1])
        endDate.setHours(23, 59, 59, 999) // Set to end of day

        filtered = filtered.filter(item => {
          const itemDate = new Date(item.createTimestamp)
          return itemDate >= startDate && itemDate <= endDate
        })
      }

      return filtered
    },

    // Computed properties for pagination
    itemsLength () {
      return this.filteredTransactions.length
    },

    pageCount () {
      return Math.ceil(this.itemsLength / this.pagination.itemsPerPage)
    },

    // Paginated transactions
    paginatedTransactions () {
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage
      const end = start + this.pagination.itemsPerPage
      return this.filteredTransactions.slice(start, end)
    }
  },
  watch: {
    // Update pagination reactive object when computed values change
    itemsLength (newValue) {
      this.pagination.itemsLength = newValue
    },

    pageCount (newValue) {
      this.pagination.pageCount = newValue
    },

    // Watch for changes in businessLayerId
    businessLayerId () {
      this.loadTransactions()
    }
  },
  mounted () {
    // Initialize with default business layer ID if available
    if (GlobalConfig.blId) {
      this.businessLayerId = GlobalConfig.blId
      this.loadTransactions()
    }
  },
  methods: {
    ...mapMutations(['showBillDetail']),

    // Load transactions
    async loadTransactions () {
      if (!this.businessLayerId) return

      this.loading = true
      try {
        const response = await BalanceTransaction.getRechargeTransactions(this.businessLayerId)
        this.transactions = response || []
        this.calculateTotalAmount()
      } catch (error) {
        console.error('Error loading recharge transactions:', error)
      } finally {
        this.loading = false
      }
    },

    // Calculate total amount from transactions
    calculateTotalAmount () {
      this.totalAmount = this.transactions.reduce((sum, transaction) => {
        return sum + (parseFloat(transaction.amount) || 0)
      }, 0)
    },

    // Open user details dialog
    openUserDetails (userId) {
      if (userId) {
        this.selectedUserId = userId
        this.userDetailsDialog = true
      }
    },

    // View transaction details
    viewDetails (transaction) {
      this.selectedTransaction = transaction
      this.detailsDialog = true
    },

    // Open bill detail dialog
    openBillDetail (orderId) {
      if (orderId) {
        this.showBillDetail(orderId)
      }
    },

    // Reset filters
    resetFilters () {
      this.transactionType = 'ALL'
      this.dateRange = [null, null]
      this.search = ''
      this.loadTransactions()
    },

    // Get transaction type display text
    getTransactionTypeText (type) {
      switch (type) {
        case 'RECHARGE':
          return '充值'
        default:
          return type
      }
    },

    // Format price using the priceDisplay filter
    formatPrice (price) {
      return this.$options.filters.priceDisplay(price)
    }
  }
}
</script>

<template>
  <div>
    <v-card flat>
      <v-card-title class="d-flex align-center">
        <date-range-picker
          v-model="dateRange"
          class="mr-4"
          style="min-width: 180px;"
        />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="搜索"
          outlined
          dense
          hide-details
          class="mr-4"
          style="max-width: 300px;"
        />
        <v-spacer />
        <div class="d-flex align-center">
          <span class="subtitle-1 mr-2">总充值金额:</span>
          <span class="headline font-weight-bold primary--text">{{ formattedTotalAmount }}</span>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                small
                color="grey"
                class="ml-1"
                v-bind="attrs"
                v-on="on"
              >
                mdi-help-circle-outline
              </v-icon>
            </template>
            <span>总充值金额是当前筛选条件下所有交易金额的总和</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>

        <!-- Data table -->
        <v-data-table
          :headers="headers"
          :items="paginatedTransactions"
          :loading="loading"
          :server-items-length="pagination.itemsLength"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100],
          }"
          class="elevation-1"
        >
          <template #item.amount="{ item }">
            <price-table-display :price="item.amount" />
          </template>

          <template #item.transactionType="{ item }">
            <v-chip
              color="success"
              small
              text-color="white"
            >
              {{ getTransactionTypeText(item.transactionType) }}
            </v-chip>
          </template>

          <template #item.rechargePackageId="{ item }">
            {{ item.rechargePackageId || '无' }}
          </template>

          <template #item.paymentIntentId="{ item }">
            {{ item.paymentIntentId ? item.paymentIntentId.substring(0, 10) + '...' : '无' }}
          </template>

          <template #item.createTimestamp="{ item }">
            {{ new Date(item.createTimestamp).toLocaleString() }}
          </template>

          <template #item.actions="{ item }">
            <v-btn
              small
              text
              color="primary"
              @click="openUserDetails(item.userId)"
              class="mr-2"
            >
              查看用户
            </v-btn>
            <v-btn
              v-if="item.orderId"
              small
              text
              color="info"
              @click="openBillDetail(item.orderId)"
            >
              查看订单
            </v-btn>
          </template>

          <template #no-data>
            <v-alert
              type="info"
              dense
              text
            >
              暂无充值交易数据
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Transaction details dialog -->
      <v-dialog
        v-model="detailsDialog"
        max-width="600px"
      >
        <v-card v-if="selectedTransaction">
          <v-card-title>
            交易详情
            <v-spacer />
            <v-btn
              icon
              @click="detailsDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <display-info-title-show
              title="基本信息"
              :items="[
                { label: '交易ID', value: selectedTransaction.id },
                { label: '用户ID', value: selectedTransaction.userId },
                { label: '用户会员ID', value: selectedTransaction.userMembershipId },
                { label: '交易金额', value: formatPrice(selectedTransaction.amount) },
                { label: '交易类型', value: getTransactionTypeText(selectedTransaction.transactionType) },
                { label: '交易描述', value: selectedTransaction.description },
                { label: '创建时间', value: new Date(selectedTransaction.createTimestamp).toLocaleString() }
              ]"
            />

            <display-info-title-show
              title="关联信息"
              :items="[
                { label: '充值套餐ID', value: selectedTransaction.rechargePackageId || '无' },
                { label: '支付意图ID', value: selectedTransaction.paymentIntentId || '无' }
              ]"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card>

    <!-- User Details Dialog -->
    <user-details-dialog
      v-model="userDetailsDialog"
      :userId="selectedUserId"
    />
  </div>
</template>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}
</style>
